<template>
  <div class="products-page">
    <!-- Page Header with Banner -->
    <div class="page-banner">
      <div class="overlay">
        <div class="container banner-container">
          <h1 class="page-title">{{ $t('header.product') }}</h1>
        </div>
      </div>
    </div>

    <div class="container">
      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner"></div>
        <p>{{ $t('common.loading') }}</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-container">
        <p>{{ error }}</p>
        <button @click="fetchProducts" class="retry-btn">{{ $t('common.retry') }}</button>
      </div>

      <!-- Empty State -->
      <div v-else-if="products.length === 0" class="empty-container">
        <p>{{ $t('common.noProducts') }}</p>
      </div>

      <!-- Products Grid -->
      <div v-else class="product-slider">
        <button v-if="products.length > 1" class="slider-arrow slider-prev outside" @click="prevProduct" :disabled="loading">
          <span>&lt;</span>
        </button>

        <div class="product-card">
          <div class="product-image-container">
            <img
              :src="currentProduct.productImage ? getFullImageUrl(currentProduct.productImage) : require('@/assets/images/l1.png')"
              :alt="currentProduct.productName || 'Tire Product'"
              class="product-image"
            >
          </div>
          <div class="product-info">
            <h3 class="product-title">{{ currentProduct.productName }}</h3>
            <p class="product-subtitle" :style="{color: '#ed1c24'}">{{ currentProduct.productCarType }}</p>
            <p class="product-manufacturer">{{ currentProduct.productSellingPoints }}</p>
            <div class="product-detail">
              <a :href="currentProduct.jumpUrl || '#'" target="_blank" class="buy-now-btn">{{ $t('common.buyNow') }}</a>
              <button class="text-btn details-btn" @click="viewProductDetail(currentProduct.id)">{{ $t('common.learnMore') }} <span class="btn-arrow">→</span></button>
            </div>
          </div>
        </div>

        <button v-if="products.length > 1" class="slider-arrow slider-next outside" @click="nextProduct" :disabled="loading">
          <span>&gt;</span>
        </button>
      </div>

      <!-- Pagination -->
      <div class="pagination" v-if="totalPages > 1">
        <button
          class="page-btn prev"
          :disabled="currentPage === 1"
          @click="changePage(currentPage - 1)"
        >
          &lt;
        </button>

        <button
          v-for="page in displayedPages"
          :key="page"
          class="page-btn"
          :class="{ active: currentPage === page }"
          @click="changePage(page)"
        >
          {{ page }}
        </button>

        <button
          class="page-btn next"
          :disabled="currentPage === totalPages"
          @click="changePage(currentPage + 1)"
        >
          &gt;
        </button>
      </div>
    </div>

    <!-- 页脚 -->
    <SiteFooter />
  </div>
</template>

<script>
import SiteFooter from '@/components/SiteFooter.vue';

export default {
  name: 'ProductsPage',
  components: {
    SiteFooter
  },
  data() {
    return {
      products: [],
      categories: [],
      loading: false,
      error: null,
      activeCategory: null,
      currentPage: 1,
      pageSize: 12,
      total: 0,
      currentProductIndex: 0,
    };
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize);
    },
    displayedPages() {
      const pages = [];
      const maxPages = 5;

      if (this.totalPages <= maxPages) {
        // Show all pages if less than maxPages
        for (let i = 1; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Always include first page
        pages.push(1);

        // Calculate start and end page numbers
        let start = Math.max(2, this.currentPage - 1);
        let end = Math.min(this.totalPages - 1, this.currentPage + 1);

        // Add ellipsis if needed before start
        if (start > 2) {
          pages.push('...');
        }

        // Add middle pages
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }

        // Add ellipsis if needed after end
        if (end < this.totalPages - 1) {
          pages.push('...');
        }

        // Always include last page
        pages.push(this.totalPages);
      }

      return pages;
    },
    currentProduct() {
      return this.products.length > 0 ? this.products[this.currentProductIndex] : {};
    }
  },
  created() {
    this.fetchCategories();
    this.fetchProducts();

    // Check if there's a category in the URL
    const categoryId = this.$route.query.categoryId;
    if (categoryId) {
      this.activeCategory = categoryId;
    }
  },
  methods: {
    fetchCategories() {
      this.$api.product.getProductCategories()
        .then(response => {
          if (response.code === 200) {
            this.categories = response.rows || [];
          } else {
            console.error('获取产品分类失败:', response.msg);
          }
        })
        .catch(err => {
          console.error('获取产品分类请求错误:', err);
        });
    },

    fetchProducts() {
      this.loading = true;
      this.error = null;

      const params = {
        pageNum: this.currentPage,
        pageSize: this.pageSize
      };

      // Add category filter if activeCategory is set
      if (this.activeCategory) {
        params.productCategory = this.activeCategory;
      }

      this.$api.product.getProductList(params)
        .then(response => {
          if (response.code === 200) {
            this.products = response.rows || [];
            this.total = response.total || 0;
          } else {
            throw new Error(response.msg || '获取产品列表失败');
          }
        })
        .catch(err => {
          console.error('获取产品列表失败:', err);
          this.error = '获取产品失败，请重试';
        })
        .finally(() => {
          this.loading = false;
        });
    },

    setCategory(categoryId) {
      this.activeCategory = categoryId;
      this.currentPage = 1; // Reset to first page when changing category

      // Update URL with category parameter
      this.$router.push({
        query: { ...this.$route.query, categoryId }
      });

      this.fetchProducts();
    },

    changePage(page) {
      if (page === '...') return;

      this.currentPage = page;

      // Update URL with page parameter
      this.$router.push({
        query: { ...this.$route.query, page }
      });

      this.fetchProducts();
    },

    viewProductDetail(productId) {
      this.$router.push(`/product/${productId}`);
    },

    getProductImage(imagePath) {
      if (!imagePath) {
        // Use the first tire image as placeholder if no specific placeholder exists
        return require('@/assets/images/l1.png');
      }

      // Check if path already has the base URL
      if (imagePath.startsWith('http')) {
        return imagePath;
      }

      return process.env.VUE_APP_IMAGE_URL + imagePath;
    },

    getProductFeatures(product) {
      // Extract features from product
      if (product.productCarType) {
        return product.productCarType.split(',').filter(item => item.trim());
      }

      // Fallback: Return a single feature based on the product category
      return ['全新静音轮胎科技'];
    },

    prevProduct() {
      if (this.currentProductIndex > 0) {
        this.currentProductIndex--;
      } else {
        this.currentProductIndex = this.products.length - 1;
      }
    },

    nextProduct() {
      if (this.currentProductIndex < this.products.length - 1) {
        this.currentProductIndex++;
      } else {
        this.currentProductIndex = 0;
      }
    },

    getFullImageUrl(imageUrl) {
      if (!imageUrl) return '';
      if (imageUrl.startsWith('http')) return imageUrl;
      return process.env.VUE_APP_IMAGE_URL + imageUrl;
    },
  }
};
</script>

<style lang="scss" scoped>
// Variables
$primary-color: #ed1c24; // BullTire Red
$secondary-color: #222; // Dark Grey/Black
$light-color: #fff;
$gray-color: #f5f5f5;
$dark-text: #333;
$light-text: #fff;
$border-radius: 4px;

// Breakpoints
$mobile: 767px;
$tablet: 1024px;

// Page Layout
.products-page {
  width: 100%;
  min-height: 100vh;
  margin-top:70px
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
}

// Page Banner
.page-banner {
  position: relative;
  background-image: url('@/assets/images/products-list-header.png'); // Use an actual banner image
  background-size: cover;
  background-position: center;
  height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 0 10%;
  position: relative;
  margin-bottom: 40px;

  @media (max-width: $mobile) {
    background-size: cover;
    background-repeat: no-repeat;
    height: 125px;
    min-height: 125px;
  }

  .page-title {
    font-size: 40px;
  font-weight: bold;
  color: white;
  position: relative;
  z-index: 2;

    @media (max-width: $mobile) {
      font-size: 28px;
    }
  }
}

// Loading, Error, Empty States
.loading-container, .error-container, .empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  text-align: center;

  p {
    margin-top: 20px;
    font-size: 18px;
    color: $dark-text;
  }
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: $primary-color;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.retry-btn {
  margin-top: 20px;
  padding: 10px 20px;
  background-color: $primary-color;
  border: none;
  color: $light-text;
  cursor: pointer;
  border-radius: $border-radius;

  &:hover {
    background-color: darken($primary-color, 10%);
  }
}

// Product Slider Styles
.product-slider {
  position: relative;
  max-width: 1200px;
  width: 90%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 40px;
}

.slider-arrow {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  transition: all 0.3s;
  font-size: 20px;
  color: #666;

  &:hover {
    background-color: #f5f5f5;
  }

  &.outside {
    background-color: rgba(255, 255, 255, 0.8);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.slider-prev {
  left: 10px;

  &.outside {
    left: -20px;
  }
}

.slider-next {
  right: 10px;

  &.outside {
    right: -20px;
  }
}

.product-card {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  background-color: $light-color;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);

  @media (max-width: $tablet) {
    flex-direction: column;
  }
}

.product-image-container {
  flex: 0 0 45%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $light-color;
  width: 400px;
  height: 400px;
  @media (max-width: $mobile) {
    width: auto;
    height: auto;
  }
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.product-info {
  flex: 1;
  padding: 50px;
  background-color: $light-color;
  text-align: left;
  display: flex;
  flex-direction: column;
  @media (max-width: $mobile) {
    padding: 25px;
  }
}

.product-title {
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 15px;
  color: #121212;
}

.product-subtitle {
  font-size: 22px;
  font-weight: bold;
  color: #E00000;
  margin-bottom: 25px;
}

.product-manufacturer {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 30px;
}

.product-detail {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 30px;
}

.learn-more-btn {
  background-color: #d81e06;
  color: #fff;
  border: none;
  padding: 10px 25px;
  border-radius: 30px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;

  .btn-arrow {
    margin-left: 5px;
  }

  &:hover {
    background-color: darken(#d81e06, 10%);
  }
}

.buy-now-btn {
  background-color: #d81e06;
  color: #fff;
  border: none;
  padding: 10px 25px;
  border-radius: 30px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  text-decoration: none;
  width: 140px;

  &:hover {
    background-color: darken(#d81e06, 10%);
  }
}

.text-btn {
  background: none;
  border: none;
  color: #666;
  font-weight: 500;
  font-size: 14px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s;

  .btn-arrow {
    margin-left: 5px;
    transition: transform 0.3s;
  }

  &:hover {
    color: $primary-color;

    .btn-arrow {
      transform: translateX(3px);
    }
  }
}

.details-btn {
  color: #666;
}

@media (max-width: $mobile) {
  .product-slider {
    flex-direction: column;
    padding: 0 15px;
    margin: 0;
    width: 100%;
  }

  .slider-arrow {
    position: static;
    margin: 20px 10px;
    transform: none;
  }

  .product-card {
    order: 2;
  }

  .slider-prev {
    order: 1;
  }

  .slider-next {
    order: 3;
  }
}

// Pagination
.pagination {
  display: flex;
  justify-content: center;
  margin-top: 50px;
  gap: 8px;
}

.page-btn {
  width: 40px;
  height: 40px;
  border: 1px solid #ddd;
  background-color: white;
  color: $dark-text;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    border-color: $primary-color;
    color: $primary-color;
  }

  &.active {
    background-color: $primary-color;
    border-color: $primary-color;
    color: $light-text;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>

